import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { useAuthModalStore } from '../stores/useAuthModalStore';
import { ticketService } from '../services/ticketService';
import { Ticket, CreateTicketData } from '../types/ticket';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import { Button, Input, Textarea, Select, Card, Badge } from '../components/ui';
import type { SelectOption } from '../components/ui';

// Page-load entrance variants — numeric durations mirror --snapfy-duration-base (300ms) /
// --snapfy-duration-slow (500ms); easing mirrors --snapfy-ease-standard.
const headingVariants: Variants = {
    hidden: { opacity: 0, y: -12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const } },
} as const;

const contentVariants: Variants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const, delay: 0.15 } },
} as const;

const priorityOptions: SelectOption[] = [
    { value: 'low', label: 'Baixa' },
    { value: 'medium', label: 'Média' },
    { value: 'high', label: 'Alta' },
];

const SupportPage: React.FC = () => {
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuthStore();
    const { openModal: openAuthModal } = useAuthModalStore();
    const prefersReducedMotion = useReducedMotion();

    // Form state
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [type, setType] = useState<'suggestion' | 'improvement' | 'complaint'>('suggestion');
    const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');

    // UI state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // User's tickets
    const [myTickets, setMyTickets] = useState<Ticket[]>([]);
    const [isLoadingTickets, setIsLoadingTickets] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [replyMessage, setReplyMessage] = useState('');
    const [isReplying, setIsReplying] = useState(false);

    // Load user's tickets when authenticated
    useEffect(() => {
        if (isAuthenticated) {
            loadMyTickets();
        }
    }, [isAuthenticated]);

    const loadMyTickets = async () => {
        setIsLoadingTickets(true);
        try {
            const response = await ticketService.getMyTickets();
            setMyTickets(response.data);
        } catch (err) {
            console.error('Failed to load tickets', err);
        } finally {
            setIsLoadingTickets(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Check authentication - open modal if not logged in
        if (!isAuthenticated) {
            openAuthModal({
                pendingAction: () => handleSubmit(e),
            });
            return;
        }

        if (!subject.trim() || !message.trim()) {
            setError('Por favor, preencha todos os campos.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const data: CreateTicketData = {
                subject,
                message,
                type,
                priority,
            };

            await ticketService.createTicket(data);
            setSuccess(true);
            setSubject('');
            setMessage('');
            setType('suggestion');
            setPriority('medium');
            loadMyTickets();

            // Reset success after 5 seconds
            setTimeout(() => setSuccess(false), 5000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erro ao enviar ticket. Tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const openTicketDetail = async (ticketId: number) => {
        try {
            const ticket = await ticketService.getTicket(ticketId);
            setSelectedTicket(ticket);
        } catch (err) {
            console.error('Failed to load ticket', err);
        }
    };

    const handleReply = async () => {
        if (!selectedTicket || !replyMessage.trim()) return;

        setIsReplying(true);
        try {
            const updatedTicket = await ticketService.addMessage(selectedTicket.id, replyMessage);
            setSelectedTicket(updatedTicket);
            setReplyMessage('');
            loadMyTickets();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erro ao enviar resposta.');
        } finally {
            setIsReplying(false);
        }
    };

    const getStatusBadgeVariant = (status: string): 'info' | 'warning' | 'success' | 'error' | 'count' => {
        switch (status) {
            case 'open': return 'info';
            case 'in_progress': return 'warning';
            case 'waiting_user': return 'info';
            case 'waiting_support': return 'warning';
            case 'closed': return 'count';
            default: return 'count';
        }
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'suggestion': return '💡';
            case 'improvement': return '🚀';
            case 'complaint': return '⚠️';
            default: return '📝';
        }
    };

    // Reduced-motion guard: flatten entrance for users who prefer it
    const resolvedHeading = prefersReducedMotion
        ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0, transition: { duration: 0 } } } as Variants
        : headingVariants;
    const resolvedContent = prefersReducedMotion
        ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0, transition: { duration: 0 } } } as Variants
        : contentVariants;

    return (
        <div className="min-h-screen bg-snapfy-bg py-8">
            <div className="max-w-4xl mx-auto px-4">
                {/* Header */}
                <motion.div
                    className="text-center mb-8"
                    variants={resolvedHeading}
                    initial="hidden"
                    animate="visible"
                >
                    <h1 className="text-3xl font-bold text-snapfy-text-primary mb-2">Central de Suporte</h1>
                    <p className="text-snapfy-text-secondary">Envie sugestões, melhorias ou reclamações. Estamos aqui para ajudar!</p>
                </motion.div>

                {/* Success Alert */}
                {success && (
                    <div className="mb-6 p-4 bg-green-900/20 border border-green-500 rounded-lg text-green-400 flex items-center gap-3">
                        <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Ticket enviado com sucesso! Nossa equipe responderá em breve.</span>
                    </div>
                )}

                {/* Error Alert */}
                {error && (
                    <div className="mb-6 p-4 bg-snapfy-error/10 border border-snapfy-error/50 rounded-lg text-snapfy-error flex justify-between items-center">
                        <span>{error}</span>
                        <button onClick={() => setError('')} className="text-snapfy-error hover:opacity-70 transition-opacity">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                )}

                <motion.div
                    className="grid grid-cols-1 lg:grid-cols-2 gap-8"
                    variants={resolvedContent}
                    initial="hidden"
                    animate="visible"
                >
                    {/* Create Ticket Form */}
                    <Card className="p-6">
                        <h2 className="text-xl font-bold text-snapfy-text-primary mb-6 flex items-center gap-2">
                            <svg className="w-6 h-6 text-snapfy-accent-soft" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Novo Ticket
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Type Selection */}
                            <div>
                                <label className="block text-sm font-medium text-snapfy-text-secondary mb-2">Tipo</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { value: 'suggestion', label: 'Sugestão', icon: '💡' },
                                        { value: 'improvement', label: 'Melhoria', icon: '🚀' },
                                        { value: 'complaint', label: 'Reclamação', icon: '⚠️' },
                                    ].map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => setType(option.value as any)}
                                            className={`p-3 rounded-lg border transition-all text-center focus-visible:outline-none focus-visible:ring-snapfy-focus ${type === option.value
                                                    ? 'border-snapfy-primary bg-snapfy-primary-10 text-snapfy-text-primary'
                                                    : 'border-snapfy-border bg-snapfy-inner text-snapfy-text-secondary hover:border-snapfy-border-strong'
                                                }`}
                                        >
                                            <div className="text-2xl mb-1">{option.icon}</div>
                                            <div className="text-xs">{option.label}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Subject */}
                            <Input
                                label="Assunto"
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Resumo do seu ticket"
                                maxLength={255}
                            />

                            {/* Message */}
                            <div>
                                <Textarea
                                    label="Mensagem"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Descreva em detalhes..."
                                    rows={5}
                                    maxLength={5000}
                                    className="resize-none"
                                />
                                <div className="text-xs text-snapfy-text-secondary text-right mt-1">{message.length}/5000</div>
                            </div>

                            {/* Priority */}
                            <div>
                                <label className="block text-sm font-medium text-snapfy-text-secondary mb-2">Prioridade</label>
                                <Select
                                    options={priorityOptions}
                                    value={priority}
                                    onChange={(val) => setPriority(val as 'low' | 'medium' | 'high')}
                                />
                            </div>

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                variant="solid"
                                disabled={isSubmitting}
                                isLoading={isSubmitting}
                                className="w-full"
                            >
                                {!isSubmitting && !isAuthenticated && (
                                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                    </svg>
                                )}
                                {isSubmitting ? 'Enviando...' : (isAuthenticated ? 'Enviar Ticket' : 'Fazer Login e Enviar')}
                            </Button>
                        </form>
                    </Card>

                    {/* My Tickets */}
                    <Card className="p-6">
                        <h2 className="text-xl font-bold text-snapfy-text-primary mb-6 flex items-center gap-2">
                            <svg className="w-6 h-6 text-snapfy-accent-soft" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Meus Tickets
                        </h2>

                        {!isAuthenticated ? (
                            <div className="text-center py-8 text-snapfy-text-secondary">
                                <svg className="w-16 h-16 mx-auto mb-4 text-snapfy-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <p className="mb-4">Faça login para ver seus tickets</p>
                                <Button
                                    variant="ghost"
                                    onClick={() => openAuthModal({})}
                                >
                                    Fazer Login
                                </Button>
                            </div>
                        ) : isLoadingTickets ? (
                            <div className="flex items-center justify-center py-8">
                                <svg className="animate-spin h-8 w-8 text-snapfy-primary" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        ) : myTickets.length === 0 ? (
                            <div className="text-center py-8 text-snapfy-text-secondary">
                                <svg className="w-16 h-16 mx-auto mb-4 text-snapfy-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                </svg>
                                <p>Você ainda não tem tickets</p>
                            </div>
                        ) : (
                            <div className="space-y-3 max-h-[500px] overflow-y-auto">
                                {myTickets.map((ticket) => (
                                    <Card
                                        key={ticket.id}
                                        interactive
                                        className="p-4 cursor-pointer focus-visible:outline-none focus-visible:ring-snapfy-focus"
                                        onClick={() => openTicketDetail(ticket.id)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTicketDetail(ticket.id); } }}
                                    >
                                        <div className="flex items-start gap-3">
                                            <span className="text-2xl">{getTypeIcon(ticket.type)}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-snapfy-text-primary font-medium truncate">{ticket.subject}</div>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Badge variant={getStatusBadgeVariant(ticket.status)}>
                                                        {ticket.statusLabel}
                                                    </Badge>
                                                    <span className="text-xs text-snapfy-text-secondary">
                                                        {new Date(ticket.createdAt).toLocaleDateString('pt-BR')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Card>
                </motion.div>
            </div>

            {/* Ticket Detail Modal — native overlay kept: holds unsaved reply state,
                no auto-close on overlay click/Esc by original design; restyle via tokens only */}
            {selectedTicket && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-snapfy-surface rounded-card shadow-card border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-snapfy-border flex justify-between items-start">
                            <div>
                                <h2 className="text-xl font-bold text-snapfy-text-primary flex items-center gap-2">
                                    <span>{getTypeIcon(selectedTicket.type)}</span>
                                    {selectedTicket.subject}
                                </h2>
                                <div className="flex gap-2 mt-2">
                                    <Badge variant={getStatusBadgeVariant(selectedTicket.status)}>
                                        {selectedTicket.statusLabel}
                                    </Badge>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedTicket(null)}
                                className="text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors focus-visible:outline-none focus-visible:ring-snapfy-focus rounded"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {selectedTicket.messages?.map((msg) => (
                                <div
                                    key={msg.id}
                                    className={`flex ${msg.isFromAdmin ? 'justify-start' : 'justify-end'}`}
                                >
                                    <div className={`max-w-[80%] rounded-card p-4 border border-white/10 ${msg.isFromAdmin ? 'bg-snapfy-primary-10' : 'bg-snapfy-inner'}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`text-sm font-medium ${msg.isFromAdmin ? 'text-snapfy-accent-soft' : 'text-snapfy-text-primary'}`}>
                                                {msg.isFromAdmin ? 'Suporte' : 'Você'}
                                            </span>
                                            {msg.isFromAdmin && (
                                                <Badge variant="info">Equipe</Badge>
                                            )}
                                        </div>
                                        <p className="text-snapfy-text-secondary whitespace-pre-wrap">{msg.message}</p>
                                        <div className="text-xs text-snapfy-text-secondary mt-2">
                                            {new Date(msg.createdAt).toLocaleString('pt-BR')}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Reply Form */}
                        {selectedTicket.isOpen && (
                            <div className="p-6 border-t border-snapfy-border">
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <Textarea
                                            value={replyMessage}
                                            onChange={(e) => setReplyMessage(e.target.value)}
                                            placeholder="Digite sua resposta..."
                                            rows={2}
                                            className="resize-none"
                                        />
                                    </div>
                                    <Button
                                        variant="solid"
                                        onClick={handleReply}
                                        disabled={!replyMessage.trim() || isReplying}
                                        isLoading={isReplying}
                                        className="self-end"
                                    >
                                        {isReplying ? 'Enviando...' : 'Enviar'}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Closed ticket info */}
                        {!selectedTicket.isOpen && (
                            <div className="p-6 border-t border-snapfy-border bg-snapfy-inner/50">
                                <div className="text-center text-snapfy-text-secondary">
                                    Este ticket foi encerrado
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupportPage;
